<template>
  <b-container>
    <h1>Publications</h1>

    <p>
      Peer-reviewed publications, white papers, presentations, and websites are
      just some of the ways project managers spread the word about their
      projects. Below are instructions for citing CitSci in your materials and a
      list of publications that CitSci has been a part of.
    </p>

    <h2>How to Cite</h2>

    <p>
      As per our <b-link to="/termsandprivacy#data">Data Use Policy</b-link>, all research publications using data from
      projects hosted on the CitSci platform (those listed on our Projects page)
      are required to cite the use of the platform. Citation provides CitSci
      credit as an integral component of methods and data collection.
      Recognition through citations is one metric funders use to evaluate our
      value and ensure that we can continue to provide data collection and
      support services to partners like you for free.
    </p>

    <h3>Acknowledgement</h3>

    <blockquote class="blockquote text-left citation-blockquote">
      <footer class="blockquote-footer">
        &lt;&lt;Your Project Name&gt;&gt; uses CitSci for hosting, collecting,
        and sharing data. CitSci.org: A global citizen science support platform.
        The development of the CitSci platform and mobile apps was funded in
        part by the National Science Foundation, Colorado State University, and
        other generous supporters.
      </footer>
    </blockquote>

    <h3>Citation</h3>

    <blockquote class="blockquote text-left citation-blockquote">
      <footer class="blockquote-footer">
        <cite title="CitSci.org">CitSci.org. 2007. CitSci.org: An online database of citizen science
          observations [web application]. CitSci.org, Fort Collins, Colorado.
          Available at: https://citsci.org. (Accessed: Date [e.g., February 2,
          2018]).</cite>
      </footer>
    </blockquote>

    <h3>Logo</h3>

    <p>
      Please use the Powered By CitSci logo below on print and web materials.
    </p>

    <!--<b-img class="bg-dark" src="/assets/images/PoweredByCitSci-white-font-transparent.png" style="width:100px;"/>

    <b-img class="" src="/assets/images/PoweredByCitSci-black-font-transparent.png" style="width:100px;"/>-->

    <div class="container pb-0 w-50 ml-0 mb-5">
      <div class="card-group">
        <div class="card w-25">
          <img class="card-img-top bg-dark" src="/assets/images/PoweredByCitSci-white-font-transparent.png"
            alt="Card image cap" />
          <div class="card-body">
            <h5 class="card-title">White Text Transparent Background</h5>
            <p class="card-text">
              <small class="text-muted">400 x 400 pixels</small>
            </p>
          </div>
        </div>
        <div class="card w-25">
          <img class="card-img-top" src="/assets/images/PoweredByCitSci-black-font-transparent.png"
            alt="Card image cap" />
          <div class="card-body">
            <h5 class="card-title">Dark Grey Text Transparent Background</h5>
            <p class="card-text">
              <small class="text-muted">400 x 400 pixels</small>
            </p>
          </div>
        </div>
      </div>
    </div>

    <h3>Let us know when you publish</h3>

    <p>
      Recently published a paper with data from CitSci?
      <ContactUsIconLink hyperlink-text="Let us know"></ContactUsIconLink>
      and we'll share it in the Publications section below. If you have any
      additional questions about how to acknowledge and reference CitSci.org,
      please feel free to <b-link to="contact">Contact Us</b-link>.
    </p>

    <h2>Publications</h2>
    <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Lin Hunter, D. E., G. J. Newman, and M. M. Balgopal. 2023. What's in a name? The paradox of citizen science and community science. Frontiers in Ecology and the Environment 21:244-250.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Alja'fari, J., Sharvelle, S., Brinkman, N. E., Jahne, M., Keely, S., Wheaton, E. A., Garland, J., Welty, C., Sukop, M. C., & Meixner, T. (2022). Characterization of roof runoff microbial quality in four U.S. cities with varying climate and land use characteristics. Water Research, 225, 119123. <a href="https://doi.org/10.1016/j.watres.2022.119123" target="_blank">https://doi.org/10.1016/j.watres.2022.119123</a>
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Nowak, K., J. Berger, A. Panikowski, D. G. Reid, A. L. Jacob, G. Newman, N. E. Young, J. P. Beckmann, and S. A. Richards. 2020. Using community photography to investigate phenology: A case study of coat molt in the mountain goat (Oreamnos americanus) with missing data. Ecology and Evolution 10:13488-13499.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Smith et al. 2019. Alternatives to genetic affinity as a context for within-species response to climate. Nature Climate Change. Vol. 9. pp. 787-794
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Austen, Lin, Forrest. 2018. On the ecological significance of pollen color: a case study in American trout lily (Erythronium americanum). Ecology 99(4). <a href="https://doi.org/10.1002/ecy.2164" target="_blank">https://doi.org/10.1002/ecy.2164</a>
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Xoco A. Shinbrot, Kelly W. Jones, Greg Newman & Miriam Ramos-Escobedo. 2021. Why citizen scientists volunteer: the influence of motivations, barriers, and perceived project relevancy on volunteer participation and retention from a novel experiment, Journal of Environmental Planning and Management, DOI: 10.1080/09640568.2021.1979944
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Liebenberg, L., //Ao, . /Am ., Lombard, M., Shermer, M., Xhukwe, . /Uase ., Biesele, M., //xao, D., Carruthers, P., Kxao, . ≠Oma ., Hansson, S.O., Langwane, H. (Karoha) ., Elbroch, L.M., /Ui, N., Keeping, D., Humphrey, G., Newman, G., G/aq'o, . /Ui ., Steventon, J., Kashe, N., Stevenson, R., Benadie, K., du Plessis, P., Minye, J., /Kxunta, . /Ui ., Ludwig, B., Daqm, . ≠Oma ., Louw, M., Debe, D. and Voysey, M., 2021. Tracking Science: An Alternative for Those Excluded by Citizen Science. Citizen Science: Theory and Practice, 6(1), p.6. DOI: <a href="http://doi.org/10.5334/cstp.284" target="_blank">http://doi.org/10.5334/cstp.284</a>
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Lin Hunter, D. E., Newman, G. J., & Balgopal, M. M. 2020. Citizen Scientist or Citizen Technician: A Case Study of Communication on One Citizen Science Platform. Citizen Science: Theory and Practice, 5(1), 17. DOI: <a href="https://doi.org/10.5334/cstp.261" target="_blank">https://doi.org/10.5334/cstp.261</a>
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G., S. Newman, R. Scarpino, N. Kaplan, and A. Crall. 2020. Data Management and Visualization In: Handbook of Citizen Science in Ecology and Conservation. University of California Press. 2020. Christopher A. Lepczyk, Owen D. Boyle, and T. L. V. Vargo (editors).
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Crall, A., D. Mellor, S. Gray, and G. Newman. 2020. Collecting High-Quality Data In: Handbook of Citizen Science in Ecology and Conservation. University of California Press. 2020. Christopher A. Lepczyk, Owen D. Boyle, and T. L. V. Vargo (editors).
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Lynn, S. J., N. Kaplan, S. Newman, R. Scarpino, and G. Newman. 2019. Designing a Platform for Ethical Citizen Science: A Case Study of CitSci.org. Citizen Science: Theory and Practice, 4(1), 14. . Citizen Science: Theory and Practice 4:14.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Jordan, R., Crall, A., Hmelo-Silver, C.E., Gray, S. A., Newman, G., Sorensen, A. 2018. Developing Model-Building as a Scientific Practice in Collaborative Citizen Science. Natural Sciences Education 47(180013): 1-7.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G., M. Chandler, M. Clyde, B. McGreavy, M. Haklay, H. Ballard, S. Gray, R. Scarpino, R. Hauptfeld, D. Mellor, and J. Gallo. 2017. Leveraging the power of place in citizen science for effective conservation decision making. Biological Conservation 208:55-64.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Frensley, T., A. Crall, M. Stern, R. Jordan, S. Gray, M. Prysby, G. Newman, C. Hmelo-Silver, D. Mellor, and J. Huang. 2017. Bridging the Benefits of Online and Community Supported Citizen Science: A Case Study on Motivation and Retention with Conservation-Oriented Volunteers. Citizen Science: Theory and Practice 2:1-14.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Chandler, M., L. See, K. Copas, A. M. Z. Bonde, B. C. López, F. Danielsen, J. K. Legind, S. Masinde, A. J. Miller-Rushing, G. Newman, A. Rosemartin, and E. Turak. 2016. Contribution of citizen science towards international biodiversity monitoring. Biological Conservation Available online 2 November 2016.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Gray, S., R. Jordan, A. Crall, G. Newman, Hmelo-Silver, H. C., J., , W. Novak, D. Mellor, T. Frensley, M. Prysby, and A. Singer. 2016. Combining participatory modelling and citizen science to support volunteer conservation action. Biological Conservation. <a href="http://dx.doi.org/10.1016/j.biocon.2016.07.037" target="_blank">http://dx.doi.org/10.1016/j.biocon.2016.07.037</a>
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G. 2014. Citizen CyberScience – New directions and opportunities for human computation. Human Computation 1:2: 103-109. DOI: 10.15346/hc.v1i2.2.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G., A. Wiggins, A. Crall, E. Graham, S. Newman, and K. Crowston. 2012. The Future of Citizen Science: Emerging technologies and shifting paradigms. Frontiers in Ecology and the Environment. 10(6): 298–304, DOI: 10.1890/110294
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Crall, A. W., K. Holfelder, G. Newman, J. Graham, and D. M. Waller. 2012. The Impacts of an Invasive Species Citizen Science Training Program on Participant Attitudes, Behavior, and Science Literacy. Public Understanding of Science. 22(6) 745-764. DOI: 0963662511434894.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G., J. Graham, A. Crall, and M. Laituri. 2011. The art and science of multi-scale citizen science support. Ecological Informatics 6:217-227.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Crall, A., G. Newman, D. M. Waller, T. J. Stohlgren, K. Holfelder, and J. Graham. 2011. Assessing Citizen Science Data Quality: An Invasive Species Case Study. Conservation Letters 4:433-442.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Wiggins, A., R. D. Stevenson, G. Newman, and K. Crowston. 2011. Mechanisms for Data Quality and Validation in Citizen Science. Paper presented at "Computing for Citizen Science" workshop. IEEE eScience Conference. Stockholm, SE, 5 December, 2011.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G., D. E. Zimmerman, A. Crall, M. Laituri, J. Graham, and L. Stapel. 2010. User friendly web mapping: Lessons from a citizen science website. International Journal of Geographical Information Science 24:1851-1869.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Newman, G. A. Crall, M. Laituri, J. Graham, T. J. Stohlgren, J. C. Moore, K. Kodrich, and K. Holfelder. 2010. Teaching citizen science skills online: Implications for invasive species training programs. Applied Environmental Education and Communication 9:4, 276-286.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Hale, C. M. 2008. Evidence for human-mediated dispersal of exotic earthworms: support for exploring strategies to limit further spread. Molecular Ecology 17:1165-1167.
            </cite>
        </blockquote>

        <blockquote class="blockquote text-left hangingindent w-75 pt-0 pb-0">
            <cite title="CitSci.org">
                Bove, Dana J., et al. "Nesting Bald Eagle Population Numbers, Density, Territorial Resources, and Relationship to Human Development in Northern Colorado's Front Range." Western Birds 55.1 (2024)
            </cite>
        </blockquote>
  </b-container>
</template>

<script>
import ContactUsIconLink from "../layout/ContactUsIconLink.vue";

export default {
  components: {ContactUsIconLink},
  data() {
    return {};
  },
};
</script>
